import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Disc, Hand, Loader2, Flower2, HeartHandshake, Sprout, ArrowRight } from 'lucide-react';

const CeramicsPottery: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Clay & Kiln";
    
    return (
        <div className="min-h-screen bg-[#e5e0d8] font-serif text-[#4a4641] selection:bg-[#a68a6d] selection:text-white overflow-hidden">
            
            {/* Organic Shape Backgrounds */}
            <div className="fixed top-[-10%] right-[-10%] w-[50vh] h-[50vh] bg-[#d3cec6] rounded-full blur-[80px] opacity-60 z-0"></div>
            <div className="fixed bottom-[-10%] left-[-10%] w-[60vh] h-[60vh] bg-[#cfc8bc] rounded-full blur-[80px] opacity-60 z-0"></div>

            {/* Navbar */}
            <nav className="relative z-10 px-8 py-8 flex justify-between items-center max-w-7xl mx-auto">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-[#4a4641] rounded-full flex items-center justify-center text-[#e5e0d8]">
                        <Disc size={20} className="animate-spin-slow" />
                    </div>
                    <span className="font-normal text-2xl tracking-wide italic">{schoolName}</span>
                </div>

                <div className="hidden md:flex gap-12 text-sm font-sans tracking-widest uppercase text-[#7a7671]">
                    <a href="#" className="hover:text-[#4a4641] transition-colors">Studio</a>
                    <a href="#" className="hover:text-[#4a4641] transition-colors">Workshops</a>
                    <a href="#" className="hover:text-[#4a4641] transition-colors">Gallery</a>
                    <a href="#" className="hover:text-[#4a4641] transition-colors">Shop</a>
                </div>

                <Button variant="outline" className="rounded-full border-[#7a7671] text-[#4a4641] hover:bg-[#4a4641] hover:text-[#e5e0d8] px-8 py-6 font-sans text-xs uppercase tracking-widest transition-colors">
                    Book Class
                </Button>
            </nav>

            {/* Hero Section */}
            <header className="relative z-10 py-20 px-8 max-w-7xl mx-auto">
                 <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center">
                     <div>
                         <span className="font-sans text-xs uppercase tracking-[0.2em] text-[#8c857b] mb-6 block">Handcrafted Education</span>
                         <h1 className="text-6xl md:text-8xl font-normal leading-tight mb-8 text-[#2c2a27]">
                             Shape <br/> 
                             <span className="italic font-light ml-8 relative">
                                 Your Vision.
                                 <svg className="absolute -bottom-2 left-0 w-full h-3 text-[#a68a6d] opacity-50" viewBox="0 0 100 10" preserveAspectRatio="none">
                                     <path d="M0 5 Q 50 10 100 5" stroke="currentColor" fill="none" strokeWidth="2" />
                                 </svg>
                             </span>
                         </h1>
                         <p className="text-lg text-[#5e5a55] leading-loose max-w-md mb-12 font-light">
                             Discover the therapeutic art of ceramics. From wheel throwing to hand-building, find your center in our sun-lit studio.
                         </p>
                         <div className="flex gap-4">
                             <Button className="rounded-full bg-[#a68a6d] hover:bg-[#8f755a] text-white px-8 py-6 font-sans text-xs uppercase tracking-widest">
                                 View Schedule
                             </Button>
                             <Button variant="ghost" className="rounded-full text-[#4a4641] hover:bg-white/50 px-8 py-6 font-sans text-xs uppercase tracking-widest gap-2">
                                 Meet the Artisans <ArrowRight size={14} />
                             </Button>
                         </div>
                     </div>

                     {/* Image Placeholder with Arch Shape */}
                     <div className="relative h-[600px]">
                         <div className="absolute top-0 right-0 w-[80%] h-full bg-[#dndndn] rounded-t-full overflow-hidden border border-white shadow-2xl">
                             <img 
                                src="https://images.unsplash.com/photo-1565193566173-7a64b27876e9?q=80&w=2070&auto=format&fit=crop" 
                                alt="Pottery Wheel" 
                                className="w-full h-full object-cover grayscale hover:grayscale-0 transition-all duration-1000 transform hover:scale-105"
                             />
                         </div>
                         <div className="absolute bottom-12 left-0 bg-white p-6 shadow-xl max-w-xs animate-in fade-in slide-in-from-bottom-8 duration-1000 delay-300">
                             <p className="font-serif italic text-lg leading-relaxed text-[#4a4641]">
                                 "Clay is the earth's way of speaking to us through our hands."
                             </p>
                         </div>
                     </div>
                 </div>
            </header>

            {/* Features Info */}
            <section className="relative z-10 py-24 px-8 bg-white/50 backdrop-blur-sm">
                <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-around gap-12 text-center">
                    <div className="flex flex-col items-center gap-4 group">
                        <div className="w-20 h-20 bg-[#efebe5] rounded-full flex items-center justify-center text-[#a68a6d] group-hover:bg-[#a68a6d] group-hover:text-white transition-colors duration-500">
                            <Hand size={32} strokeWidth={1} />
                        </div>
                        <h3 className="font-sans text-sm font-bold uppercase tracking-widest text-[#4a4641]">Hand Building</h3>
                        <p className="max-w-xs text-[#7a7671] text-sm leading-relaxed">Coil, slab, and pinch techniques for sculptural forms.</p>
                    </div>
                    <div className="flex flex-col items-center gap-4 group">
                        <div className="w-20 h-20 bg-[#efebe5] rounded-full flex items-center justify-center text-[#a68a6d] group-hover:bg-[#a68a6d] group-hover:text-white transition-colors duration-500">
                            <Disc size={32} strokeWidth={1} />
                        </div>
                        <h3 className="font-sans text-sm font-bold uppercase tracking-widest text-[#4a4641]">Wheel Throwing</h3>
                        <p className="max-w-xs text-[#7a7671] text-sm leading-relaxed">Master the wheel and create symmetrical vessels.</p>
                    </div>
                    <div className="flex flex-col items-center gap-4 group">
                        <div className="w-20 h-20 bg-[#efebe5] rounded-full flex items-center justify-center text-[#a68a6d] group-hover:bg-[#a68a6d] group-hover:text-white transition-colors duration-500">
                            <Flower2 size={32} strokeWidth={1} />
                        </div>
                        <h3 className="font-sans text-sm font-bold uppercase tracking-widest text-[#4a4641]">Glazing</h3>
                        <p className="max-w-xs text-[#7a7671] text-sm leading-relaxed">Advanced surface decoration and firing techniques.</p>
                    </div>
                </div>
            </section>

             {/* Footer */}
            <footer className="relative z-10 py-20 px-8 text-center">
                 <div className="max-w-md mx-auto">
                     <div className="mb-8">
                         <span className="font-serif italic text-2xl text-[#4a4641] block mb-2">{schoolName}</span>
                         <span className="font-sans text-[10px] uppercase tracking-[0.2em] text-[#8c857b]">Pottery Studio & School</span>
                     </div>
                     <div className="flex justify-center gap-8 mb-8 font-sans text-xs font-bold uppercase tracking-widest text-[#4a4641]">
                         <a href="#" className="hover:text-[#a68a6d]">Instagram</a>
                         <a href="#" className="hover:text-[#a68a6d]">Pinterest</a>
                         <a href="#" className="hover:text-[#a68a6d]">Facebook</a>
                     </div>
                     <p className="text-[#8c857b] text-xs font-sans">
                         123 Artisan Way, Creative District <br/>
                         Open Daily 10am - 8pm
                     </p>
                 </div>
            </footer>
        </div>
    );
};

export default CeramicsPottery;
